'use client';

import { useEffect } from 'react';

// Bridges Next.js's root error boundary into the 180D Feedback Loop.
// Without this file, every React render crash is swallowed by Next.js
// before window.onerror fires — so bugsnag-client.js never sees them
// and the central inbox stays empty even though the pipeline is alive.
// See 180DegreesIT/180D_FeedbackLoop#82 for the full audit.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof (window as unknown as { __bugsnagReport?: (s: string, m: string, st: string, n?: string) => void }).__bugsnagReport === 'function'
    ) {
      try {
        // 4th arg (errorName) is what survives on sensitive/clinical routes,
        // where the client strips message+console to a name-free shape
        // (BL-001 part 2) — without it every clinical crash titles as a bare
        // "Error @ <route>" (180DegreesIT/180D_FeedbackLoop#1016). The digest
        // is Next's server-error hash (no PII) for server-log correlation.
        const name = error.name || 'ReactRenderError';
        (window as unknown as { __bugsnagReport: (s: string, m: string, st: string, n?: string) => void }).__bugsnagReport(
          'error',
          error.message || 'Unknown React render error',
          error.stack ?? '',
          error.digest ? `${name} [digest ${error.digest}]` : name,
        );
      } catch {
        /* never let the reporter crash the recovery UI */
      }
    }
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
            color: '#111827',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', maxWidth: '24rem' }}>
            An unexpected error occurred. We&apos;ve been notified — please try again.
          </p>
          <button
            onClick={() => reset()}
            style={{
              padding: '0.5rem 1.25rem',
              backgroundColor: '#111827',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
          {error.digest && (
            <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
              Error ID: {error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
